import { useEffect, useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import QRCode from 'qrcode'
import {
  LOCALES,
  type ApiToken,
  type ApiTokenScope,
  type CreateApiTokenResponse,
  type Entitlements,
  type Locale,
  type MfaStatus,
  type RecoveryCodesResponse,
} from '@ousse/shared'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useHousehold } from '../lib/household'
import { Alert, ConfirmButton, ErrorAlert, Field } from '../components/ui'

export function SettingsPage() {
  const { t, i18n } = useTranslation()
  const { me, refresh } = useAuth()
  const [error, setError] = useState<unknown>(null)
  const [saved, setSaved] = useState(false)
  const user = me!.user

  const update = async (patch: { locale?: Locale; simplifiedMode?: boolean }) => {
    setError(null)
    setSaved(false)
    try {
      await api.patch('/me', patch)
      if (patch.locale) await i18n.changeLanguage(patch.locale)
      await refresh()
      setSaved(true)
    } catch (e) {
      setError(e)
    }
  }

  return (
    <>
      <h1>{t('settings.title')}</h1>
      <ErrorAlert error={error} />
      {saved && <Alert kind="success">{t('settings.saved')}</Alert>}
      <div className="card stack">
        <div className="field">
          <label htmlFor="locale">{t('settings.language')}</label>
          <select id="locale" value={user.locale} onChange={(e) => void update({ locale: e.target.value as Locale })}>
            {LOCALES.map((l) => (
              <option key={l} value={l}>
                {t(`languages.${l}`)}
              </option>
            ))}
          </select>
        </div>
        <label className="checkbox">
          <input type="checkbox" checked={user.simplifiedMode} onChange={(e) => void update({ simplifiedMode: e.target.checked })} />
          <span>
            <strong>{t('settings.simplifiedMode')}</strong>
            <br />
            <span className="muted">{t('settings.simplifiedModeHint')}</span>
          </span>
        </label>
      </div>
      {(user.isInstanceAdmin || !user.simplifiedMode) && (
        <>
          <h2>{t('settings.security')}</h2>
          <TotpSetup enabled={user.mfaEnabled} onDone={refresh} />
        </>
      )}
      {!user.simplifiedMode && <ApiTokensSection />}
      <MyDataSection email={user.email} isAdmin={user.isInstanceAdmin} />
    </>
  )
}

/** Mes données (brief, données personnelles) : tout télécharger ; supprimer son compte, avec confirmation. */
function MyDataSection({ email, isAdmin }: { email: string; isAdmin: boolean }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [error, setError] = useState<unknown>(null)
  const [deleting, setDeleting] = useState(false)
  const [confirmEmail, setConfirmEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)

  const download = async () => {
    setError(null)
    try {
      const res = await fetch('/api/v1/me/export', { credentials: 'include' })
      if (!res.ok) throw new Error(`export_${res.status}`)
      const url = URL.createObjectURL(await res.blob())
      const a = document.createElement('a')
      a.href = url
      a.download = 'ousse-mes-donnees.json'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e)
    }
  }

  const remove = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.post('/me/delete', { confirmEmail, ...(password ? { password } : {}) })
      // Compte supprimé : plus rien de l'ancien compte dans la page, retour à la connexion.
      qc.clear()
      window.location.assign('/login')
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }

  return (
    <>
      <h2>{t('settings.myData')}</h2>
      <div className="card stack">
        <p className="muted">{t('settings.myDataHint')}</p>
        <ErrorAlert error={error} />
        <button type="button" className="btn secondary" onClick={() => void download()}>
          {t('settings.downloadMyData')}
        </button>
        {isAdmin ? (
          <p className="muted">{t('settings.deleteAccountAdmin')}</p>
        ) : !deleting ? (
          <button type="button" className="btn danger" onClick={() => setDeleting(true)}>
            {t('settings.deleteAccount')}
          </button>
        ) : (
          <form className="stack" onSubmit={(e) => void remove(e)}>
            <Alert kind="warning">{t('settings.deleteAccountWarning')}</Alert>
            <Field
              label={t('settings.deleteAccountConfirmEmail', { email })}
              type="email"
              autoComplete="off"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              required
            />
            <Field
              label={t('auth.password')}
              hint={t('settings.deleteAccountPasswordHint')}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <div className="row">
              <button
                type="submit"
                className="btn danger"
                disabled={busy || confirmEmail.trim().toLowerCase() !== email.toLowerCase()}
              >
                {t('settings.deleteAccountSubmit')}
              </button>
              <button type="button" className="btn ghost" onClick={() => setDeleting(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  )
}

function ApiTokensSection() {
  const { t, i18n } = useTranslation()
  const { current } = useHousehold()
  const qc = useQueryClient()
  const hid = current?.id
  const canWrite = Boolean(current && current.role !== 'viewer' && !current.readOnly)
  const [name, setName] = useState('')
  const [scope, setScope] = useState<ApiTokenScope>('read_write')
  const [expires, setExpires] = useState('')
  const [created, setCreated] = useState<CreateApiTokenResponse | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [copied, setCopied] = useState(false)

  const entitlements = useQuery({
    queryKey: ['entitlements', hid],
    enabled: Boolean(hid),
    queryFn: () => api.get<Entitlements>(`/households/${hid}/entitlements`),
  })

  const tokens = useQuery({
    queryKey: ['api-tokens', hid],
    enabled: Boolean(hid) && entitlements.data?.features.mcp,
    queryFn: () => api.get<{ items: ApiToken[] }>(`/households/${hid}/api-tokens`),
  })

  const create = useMutation({
    mutationFn: () =>
      api.post<CreateApiTokenResponse>(`/households/${hid}/api-tokens`, {
        name: name.trim(),
        scope,
        expiresInDays: expires.trim() ? Number(expires) : null,
      }),
    onSuccess: async (data) => {
      setCreated(data)
      setName('')
      setExpires('')
      setError(null)
      setCopied(false)
      await qc.invalidateQueries({ queryKey: ['api-tokens', hid] })
    },
    onError: setError,
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/households/${hid}/api-tokens/${id}`),
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['api-tokens', hid] })
    },
    onError: setError,
  })

  if (!hid) return null
  if (entitlements.isLoading) return <p role="status">{t('common.loading')}</p>
  if (!entitlements.data?.features.mcp) {
    return (
      <>
        <h2>{t('settings.apiTokens')}</h2>
        <Alert kind="warning">{t('settings.apiTokensLocked')}</Alert>
      </>
    )
  }

  const mcpUrl = `${window.location.origin}/mcp`

  return (
    <>
      <h2>{t('settings.apiTokens')}</h2>
      <p className="muted">{t('settings.apiTokensHint')}</p>
      <p className="muted">{t('settings.apiTokenEndpoint', { url: mcpUrl })}</p>
      <p className="muted">{t('settings.apiTokenClaudeConnector')}</p>
      <ErrorAlert error={error ?? tokens.error} />
      {created && (
        <Alert kind="success">
          <p>{t('settings.apiTokenCreated')}</p>
          <p className="code" style={{ wordBreak: 'break-all' }}>
            {created.token}
          </p>
          <button
            type="button"
            className="btn secondary"
            onClick={async () => {
              await navigator.clipboard.writeText(created.token)
              setCopied(true)
            }}
          >
            {copied ? t('common.copied') : t('common.copy')}
          </button>
        </Alert>
      )}
      {canWrite && (
        <form
          className="card stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            if (!name.trim()) return
            create.mutate()
          }}
        >
          <Field
            label={t('settings.apiTokenName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('settings.apiTokenNamePlaceholder')}
            maxLength={80}
            required
          />
          <div className="field">
            <label htmlFor="token-scope">{t('settings.apiTokenScope')}</label>
            <select
              id="token-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value as ApiTokenScope)}
            >
              <option value="read">{t('settings.apiTokenScopeRead')}</option>
              <option value="read_write">{t('settings.apiTokenScopeReadWrite')}</option>
            </select>
          </div>
          <Field
            label={t('settings.apiTokenExpires')}
            value={expires}
            onChange={(e) => setExpires(e.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
          />
          <button className="btn" disabled={create.isPending}>
            {t('settings.apiTokenCreate')}
          </button>
        </form>
      )}
      {tokens.isLoading && <p role="status">{t('common.loading')}</p>}
      {tokens.data && tokens.data.items.length === 0 && <p className="muted">{t('settings.apiTokenEmpty')}</p>}
      {tokens.data && tokens.data.items.length > 0 && (
        <ul className="list">
          {tokens.data.items.map((tok) => (
            <li key={tok.id} className="row" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
              <span style={{ flex: 1 }}>
                <strong>{tok.name}</strong>
                <br />
                <span className="muted">
                  {tok.scope === 'read' ? t('settings.apiTokenScopeRead') : t('settings.apiTokenScopeReadWrite')}
                  {' · '}
                  {tok.lastUsedAt
                    ? t('settings.apiTokenLastUsed', {
                        when: new Date(tok.lastUsedAt).toLocaleString(i18n.language),
                      })
                    : t('settings.apiTokenNeverUsed')}
                </span>
              </span>
              {canWrite && (
                <ConfirmButton
                  message={t('settings.apiTokenRevokeConfirm', { name: tok.name })}
                  onConfirm={() => revoke.mutate(tok.id)}
                >
                  {t('settings.apiTokenRevoke')}
                </ConfirmButton>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

/**
 * Code de sécurité (TOTP, obligatoire pour l'administration) : activation, puis codes de récupération (montrés une
 * seule fois), nouveaux codes, désactivation (code du moment ou code de récupération, avec confirmation).
 */
export function TotpSetup({ enabled, onDone }: { enabled: boolean; onDone: () => Promise<void> }) {
  const { t } = useTranslation()
  const [setup, setSetup] = useState<{ otpauthUrl: string; secret: string } | null>(null)
  const [qr, setQr] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<unknown>(null)
  // Codes de récupération tout juste créés : affichés jusqu'à « C'est noté ».
  const [codes, setCodes] = useState<string[] | null>(null)
  useEffect(() => {
    if (setup) void QRCode.toDataURL(setup.otpauthUrl, { margin: 1, width: 192 }).then(setQr)
  }, [setup])

  if (codes) return <RecoveryCodesShown codes={codes} onDone={() => setCodes(null)} />
  if (enabled) return <MfaManage onCodes={setCodes} onDone={onDone} />
  if (!setup) {
    return (
      <>
        <ErrorAlert error={error} />
        <button
          type="button"
          className="btn secondary"
          onClick={async () => {
            try {
              setSetup(await api.post('/auth/mfa/totp/setup'))
            } catch (e) {
              setError(e)
            }
          }}
        >
          {t('settings.mfaSetup')}
        </button>
      </>
    )
  }
  return (
    <form
      className="card stack"
      onSubmit={async (e) => {
        e.preventDefault()
        try {
          const res = await api.post<RecoveryCodesResponse>('/auth/mfa/totp/enable', { code })
          setSetup(null)
          setCode('')
          setCodes(res.recoveryCodes)
          await onDone()
        } catch (err) {
          setError(err)
        }
      }}
    >
      <ErrorAlert error={error} />
      <p>{t('settings.mfaScan')}</p>
      {qr && <img className="qr" src={qr} alt={t('settings.mfaSetup')} />}
      <p className="code" style={{ wordBreak: 'break-all' }}>
        {setup.secret}
      </p>
      <Field
        label={t('auth.code')}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="\d{6}"
        maxLength={6}
        required
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
      />
      <button className="btn">{t('auth.mfaVerify')}</button>
    </form>
  )
}

/** Codes de récupération en clair, une seule fois : copier ou télécharger, puis « C'est noté ». */
function RecoveryCodesShown({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const text = codes.join('\n')
  return (
    <section className="card stack" aria-labelledby="recovery-codes-title">
      <h3 id="recovery-codes-title" style={{ margin: 0 }}>
        {t('settings.recoveryCodesTitle')}
      </h3>
      <Alert kind="warning">{t('settings.recoveryCodesHint')}</Alert>
      <ul className="code" style={{ columns: 2, listStyle: 'none', padding: 0, margin: 0, fontSize: '1.1rem' }}>
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="row">
        <button
          type="button"
          className="btn secondary"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(text)
              setCopied(true)
            } catch {
              setCopied(false)
            }
          }}
        >
          {copied ? t('settings.recoveryCodesCopied') : t('settings.recoveryCodesCopy')}
        </button>
        <button
          type="button"
          className="btn secondary"
          onClick={() => {
            const url = URL.createObjectURL(new Blob([`${text}\n`], { type: 'text/plain' }))
            const a = document.createElement('a')
            a.href = url
            a.download = 'ousse-codes-de-recuperation.txt'
            a.click()
            URL.revokeObjectURL(url)
          }}
        >
          {t('settings.recoveryCodesDownload')}
        </button>
        <button type="button" className="btn" onClick={onDone}>
          {t('settings.recoveryCodesDone')}
        </button>
      </div>
    </section>
  )
}

/** Code de sécurité activé : codes de récupération restants, nouveaux codes, désactivation. */
function MfaManage({ onCodes, onDone }: { onCodes: (codes: string[]) => void; onDone: () => Promise<void> }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const status = useQuery({ queryKey: ['mfa-status'], queryFn: () => api.get<MfaStatus>('/auth/mfa') })
  const [mode, setMode] = useState<'regenerate' | 'disable' | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<unknown>(null)
  const left = status.data?.recoveryCodesLeft
  const close = () => {
    setMode(null)
    setCode('')
    setError(null)
  }
  const disable = async () => {
    try {
      await api.post('/auth/mfa/totp/disable', { code: code.trim() })
      close()
      await qc.invalidateQueries({ queryKey: ['mfa-status'] })
      await onDone()
    } catch (err) {
      setError(err)
    }
  }
  return (
    <div className="stack">
      <Alert kind="success">{t('settings.mfaEnabled')}</Alert>
      {left !== undefined && (
        <p className="muted" style={{ margin: 0 }}>
          {t('settings.recoveryCodesLeft', { count: left })}
        </p>
      )}
      {left === 0 && <Alert kind="warning">{t('settings.recoveryCodesNone')}</Alert>}
      {mode === null && (
        <div className="row">
          <button type="button" className="btn secondary" onClick={() => setMode('regenerate')}>
            {t('settings.recoveryCodesNew')}
          </button>
          <button type="button" className="btn secondary" onClick={() => setMode('disable')}>
            {t('settings.mfaDisable')}
          </button>
        </div>
      )}
      {mode === 'regenerate' && (
        <form
          className="card stack"
          onSubmit={async (e) => {
            e.preventDefault()
            try {
              const res = await api.post<RecoveryCodesResponse>('/auth/mfa/recovery-codes', { code })
              close()
              await qc.invalidateQueries({ queryKey: ['mfa-status'] })
              onCodes(res.recoveryCodes)
            } catch (err) {
              setError(err)
            }
          }}
        >
          <p style={{ margin: 0 }}>{t('settings.recoveryCodesNewHint')}</p>
          <ErrorAlert error={error} />
          <Field
            label={t('auth.code')}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
          <div className="row">
            <button className="btn">{t('settings.recoveryCodesNew')}</button>
            <button type="button" className="btn ghost" onClick={close}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}
      {mode === 'disable' && (
        <div className="card stack">
          <p style={{ margin: 0 }}>{t('settings.mfaDisableHint')}</p>
          <ErrorAlert error={error} />
          <Field label={t('auth.codeOrRecovery')} autoComplete="one-time-code" maxLength={20} required value={code} onChange={(e) => setCode(e.target.value)} />
          <div className="row">
            {code.trim().length >= 6 ? (
              <ConfirmButton message={t('settings.mfaDisableConfirm')} onConfirm={disable}>
                {t('settings.mfaDisable')}
              </ConfirmButton>
            ) : (
              <button type="button" className="btn danger" disabled>
                {t('settings.mfaDisable')}
              </button>
            )}
            <button type="button" className="btn ghost" onClick={close}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
